import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { createDeviceMdmCommand } from "@dashboard/devices/api";
import { useDeleteFileMutation } from "@dashboard/files/api";
import apiClient from "@dashboard/files/api/apiClient";
import ActionsMenu from "@dashboard/files/components/ActionsMenu/ActionsMenu";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { ConfirmDialog } from "@devices/components/shared";
import { selectDetailsLicense, setPaneTab } from "@devices/store";
import { Device } from "@devices/types";
import { FileInfo } from "@files/types";
import { hasUnsuccessfulAnalyzer } from "@files/utils";
import { Box, DialogContentText } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { showErrorToast } from "@shared/toasts/Toasts";

import { FileSettingsDialog } from "../../../FileSettingsDialog";

import { useStyles } from "./TableItem.style";
import { getApkFileStatus, getPrimaryButtonProps } from "./TableItem.utils";

type Props = {
    file: FileInfo;
    device: Device;
};
const ActionsCell = (props: Props) => {
    const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t } = useTranslation(["sidepanels", "modals", "common"]);

    const [deleteFile] = useDeleteFileMutation();
    const unsuccessfulAnalyzerExist = hasUnsuccessfulAnalyzer(props.file);
    const status = getApkFileStatus(props.file, props.device.lastState?.packages);

    const { deviceAbility, fileAbility } = PermissionService();
    const mayInstallApp = deviceAbility(props.device).can("install", "App");
    const mayManageTags = fileAbility(props.file).can("manage", "Tag");

    const isExpired = useCurrentSubscriptionExpired();
    const isOffline = !props.device.isOnline;

    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const handleDelete = async () => {
        await deleteFile(props.file.id);
    };

    const installPackageForDevice = async () => {
        try {
            const url = await apiClient.getPresignedDownloadUrl(props.file.id);
            const commandObject = {
                url: url,
                source: "url",
                package: props.file.metadata?.file?.apk?.manifest?.package,
                version: props.file.metadata?.file?.apk?.manifest?.versioncode,
            };

            await createDeviceMdmCommand({
                deviceId: props.device.id,
                json: {
                    command: "installPackage",
                    caption: "Install package",
                    ...commandObject,
                },
            }).then(() => {
                dispatch(setPaneTab("COMMANDS"));
            });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };
    const getTagBtnTitle = () => {
        const result = { title: t("sidepanels:devicePanel.tooltip.manageTags") };

        if (isExpired) {
            result.title = t("sidepanels:devicePanel.tooltip.subscriptionExpired");
        }
        if (!mayManageTags) {
            result.title = t("sidepanels:devicePanel.tooltip.noPermissionToManageTags");
        }
        return result;
    };

    const installButtonProps = getPrimaryButtonProps(
        status,
        unsuccessfulAnalyzerExist,
        mayInstallApp,
        props.file.metadata?.file?.apk?.manifest?.versioncode,
    );

    const getTooltipTitle = () => {
        if (isExpired) {
            return t("sidepanels:devicePanel.tooltip.subscriptionExpired");
        }
        if (isOffline) {
            return t("common:tooltip.deviceOffline");
        }
        return installButtonProps.tooltip;
    };

    return (
        <>
            <Box className={classes.buttonBox}>
                {status !== "notApk" && (
                    <SecondaryButton
                        tooltipProps={{ title: getTooltipTitle() }}
                        disabled={installButtonProps.disabled || isExpired || isOffline}
                        onClick={installPackageForDevice}
                    >
                        <i className={installButtonProps.iconClass} />
                    </SecondaryButton>
                )}

                <SecondaryButton
                    onClick={() => setIsSettingsModalOpen(true)}
                    tooltipProps={getTagBtnTitle()}
                    disabled={!mayManageTags}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>
                <ActionsMenu file={props.file} />
            </Box>

            <ConfirmDialog
                title={t("modals:deleteFile.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={isModalOpen}
                onConfirm={handleDelete}
                onClose={toggleModal}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteFile.description"
                        values={{ filename: props.file.filename }}
                        components={{ bold: <span style={{ fontWeight: "bold" }} /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>

            <FileSettingsDialog item={props.file} open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
        </>
    );
};

export default ({ file }: { file: FileInfo }) => {
    const license = useSelector(selectDetailsLicense);

    if (!license || !license.device) {
        return null;
    }

    return <ActionsCell file={file} device={license.device} />;
};
